import React from "react";
import { Box, Paper, Stack, Typography, Skeleton } from "@mui/material";
import { useDroppable } from "@dnd-kit/core";

import TicketCard from "./TicketCard";
import { COLORS, RADIUS } from "./ticketTheme";

export default function BoardColumn({
  status,
  label,
  color,
  tickets,
  loading,
  departments,
  onQuickAdd,
  onOpenTicket,
  onDeleteTicket,
}) {
  const { setNodeRef, isOver } = useDroppable({
    id: status,
    data: { status },
  });

  return (
    <Paper
      ref={setNodeRef}
      elevation={0}
      sx={{
        flex: "1 1 0",
        minWidth: { xs: 260, sm: 280 },
        display: "flex",
        flexDirection: "column",
        p: 2,
        minHeight: 620,
        maxHeight: "calc(100vh - 280px)",
        bgcolor: isOver ? COLORS.accentSoft : COLORS.cardBg,
        border: "1px solid",
        borderColor: COLORS.border,
        borderRadius: `${RADIUS.card}px`,
        transition: "background-color 120ms ease",
      }}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        mb={2}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <Box
            sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: color }}
          />
          <Typography
            variant="subtitle1"
            fontWeight={700}
            sx={{ color: COLORS.textPrimary, fontSize: 15 }}
          >
            {label}
          </Typography>
        </Stack>
        <Box
          sx={{
            bgcolor: COLORS.pageBg,
            color: COLORS.textSecondary,
            fontSize: 12,
            fontWeight: 700,
            borderRadius: `${RADIUS.pill}px`,
            minWidth: 24,
            height: 24,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: 0.75,
          }}
        >
          {tickets.length}
        </Box>
      </Stack>

      <Stack
        spacing={1.5}
        sx={{
          flex: 1,
          minHeight: 80,
          overflowY: "auto",
          pr: 0.5,
          "&::-webkit-scrollbar": { width: 6 },
          "&::-webkit-scrollbar-thumb": {
            bgcolor: COLORS.border,
            borderRadius: `${RADIUS.pill}px`,
          },
        }}
      >
        {loading ? (
          <>
            <Skeleton
              variant="rounded"
              height={128}
              sx={{ borderRadius: `${RADIUS.card}px` }}
            />
            <Skeleton
              variant="rounded"
              height={128}
              sx={{ borderRadius: `${RADIUS.card}px` }}
            />
          </>
        ) : tickets.length === 0 ? (
          <Typography
            variant="body2"
            onClick={onQuickAdd}
            sx={{
              cursor: "pointer",
              py: 1,
              color: COLORS.textDisabled,
              "&:hover": { color: COLORS.textSecondary },
            }}
          >
            Add ticket
          </Typography>
        ) : (
          tickets.map((ticket) => (
            <TicketCard
              key={ticket.id}
              ticket={ticket}
              status={status}
              departments={departments}
              onClick={() => onOpenTicket?.(ticket)}
              onDelete={onDeleteTicket}
            />
          ))
        )}
      </Stack>
    </Paper>
  );
}
